import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import enTranslations from '../locales/en.json';
import arTranslations from '../locales/ar.json';
import enPersonal from '../data/en/personal.json';
import enProjects from '../data/en/projects.json';
import enSkills from '../data/en/skills.json';
import enExperience from '../data/en/experience.json';
import arPersonal from '../data/ar/personal.json';
import arProjects from '../data/ar/projects.json';
import arSkills from '../data/ar/skills.json';
import arExperience from '../data/ar/experience.json';
import sharedProjects from '../data/shared/projects.json';
import sharedSkills from '../data/shared/skills.json';
import sharedExperience from '../data/shared/experience.json';
import sharedPersonal from '../data/shared/personal.json';
import { mergeById, mergePersonal } from '../utils/mergeData';

const STORAGE_KEY = 'portfolio-language';
const LanguageContext = createContext(undefined);

const resources = {
  en: {
    translations: enTranslations,
    data: {
      personal: mergePersonal(sharedPersonal, enPersonal),
      projects: mergeById(sharedProjects, enProjects),
      skills: mergeById(sharedSkills, enSkills),
      experience: mergeById(sharedExperience, enExperience),
    },
  },
  ar: {
    translations: arTranslations,
    data: {
      personal: mergePersonal(sharedPersonal, arPersonal),
      projects: mergeById(sharedProjects, arProjects),
      skills: mergeById(sharedSkills, arSkills),
      experience: mergeById(sharedExperience, arExperience),
    },
  },
};

const readLanguage = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved === 'ar' || saved === 'en' ? saved : 'en';
  } catch (error) {
    return 'en';
  }
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within a LanguageProvider');
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(readLanguage);
  const direction = language === 'ar' ? 'rtl' : 'ltr';
  const resource = resources[language];

  useEffect(() => {
    const root = document.documentElement;
    root.lang = language;
    root.dir = direction;
    root.dataset.language = language;
    try { localStorage.setItem(STORAGE_KEY, language); } catch (error) { console.warn('Failed to save language:', error); }
  }, [language, direction]);

  const setLanguage = useCallback(nextLanguage => {
    if (resources[nextLanguage]) setLanguageState(nextLanguage);
  }, []);
  const toggleLanguage = useCallback(() => setLanguageState(current => current === 'en' ? 'ar' : 'en'), []);
  const t = useCallback(key => {
    const value = key.split('.').reduce((current, part) => current?.[part], resource.translations);
    return typeof value === 'string' ? value : key;
  }, [resource]);
  const formatNumber = useCallback(value => new Intl.NumberFormat(language === 'ar' ? 'ar-EG' : 'en-US').format(value), [language]);

  const contextValue = useMemo(() => ({ language, direction, isRTL: direction === 'rtl', setLanguage, toggleLanguage, t, formatNumber, data: resource.data }), [language, direction, setLanguage, toggleLanguage, t, formatNumber, resource]);
  return <LanguageContext.Provider value={contextValue}>{children}</LanguageContext.Provider>;
};
